import { PAD_CHORD, DRONE_ROOT, hzFor } from '../scales.js';

/**
 * The hour itself. A wide D-minor-eleventh that never changes chord, only
 * weight: the upper voices thin out as the traveller descends and the bottom
 * of the stair fills with a drone that was not audible at the top.
 */
export class PadVoice {
  /** @param {import('../AudioEngine.js').AudioEngine} engine */
  constructor(engine) {
    this.engine = engine;
    this.ac = engine.ac;
    this.partials = [];
    this.started = false;
  }

  start() {
    if (this.started) return;
    const ac = this.ac;
    const now = ac.currentTime;

    this.out = ac.createGain();
    this.out.gain.value = 0.0001;
    this.out.connect(this.engine.dry);

    const sendTap = ac.createGain();
    sendTap.gain.value = 0.7;
    this.out.connect(sendTap);
    sendTap.connect(this.engine.send);

    this.lp = ac.createBiquadFilter();
    this.lp.type = 'lowpass';
    this.lp.frequency.value = 1400;
    this.lp.Q.value = 0.6;
    this.lp.connect(this.out);

    PAD_CHORD.forEach((note, i) => {
      const hz = hzFor(note);
      const gain = ac.createGain();
      // Upper voices start loud (youth) and are taken away.
      gain.gain.value = 0.11 / (1 + i * 0.35);
      gain.connect(this.lp);

      for (const cents of [-6, 7]) {
        const o = ac.createOscillator();
        o.type = i < 2 ? 'sine' : 'triangle';
        o.frequency.value = hz;
        o.detune.value = cents + (Math.random() - 0.5) * 3;
        o.connect(gain);
        o.start(now + i * 0.03);
        this.partials.push({ osc: o, gain, index: i, base: gain.gain.value });
      }

      // Each voice breathes on its own slow cycle.
      const lfo = ac.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.035 + i * 0.013;
      const lfoGain = ac.createGain();
      lfoGain.gain.value = gain.gain.value * 0.55;
      lfo.connect(lfoGain);
      lfoGain.connect(gain.gain);
      lfo.start(now);
      this.partials.push({ osc: lfo, gain: lfoGain, lfo: true, index: i });
    });

    // The drone that waits at the bottom.
    this.droneGain = ac.createGain();
    this.droneGain.gain.value = 0.0001;
    this.droneGain.connect(this.lp);
    const rootHz = hzFor(DRONE_ROOT);
    for (const mult of [1, 1.0012, 2]) {
      const o = ac.createOscillator();
      o.type = mult === 2 ? 'triangle' : 'sine';
      o.frequency.value = rootHz * mult;
      o.connect(this.droneGain);
      o.start(now);
      this.partials.push({ osc: o, drone: true });
    }

    this.out.gain.setTargetAtTime(0.9, now, 4.0);
    this.started = true;
  }

  update(dt, state) {
    if (!this.started) return;
    const now = this.ac.currentTime;
    const t = state.t;

    this.lp.frequency.setTargetAtTime(1700 - t * 900 + state.iris * 1800, now, 0.8);
    this.droneGain.gain.setTargetAtTime(0.0001 + Math.pow(t, 1.4) * 0.3, now, 1.2);

    // Thin the upper register out as the light goes.
    for (const p of this.partials) {
      if (p.lfo || p.drone || p.index === undefined) continue;
      const fade = p.index >= 3 ? 1 - t * 0.72 : 1 - t * 0.2;
      p.gain.gain.setTargetAtTime(Math.max(p.base * fade, 0.00001), now, 1.5);
    }
  }

  stop() {
    if (!this.started) return;
    const now = this.ac.currentTime;
    this.out.gain.setTargetAtTime(0.00001, now, 0.5);
    for (const p of this.partials) p.osc.stop(now + 2);
    this.started = false;
  }
}
